package com.example.event_management_system.ui.screens.login

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.koin.getScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.example.event_management_system.ui.components.common.SmallLoadingIndicator
import com.example.event_management_system.ui.screens.calendar.CalendarScreen
import com.example.event_management_system.ui.screens.location.LocationPickerScreen
import com.example.event_management_system.ui.theme.Dimens
import event_management_system.composeapp.generated.resources.Res
import event_management_system.composeapp.generated.resources.*
import org.jetbrains.compose.resources.stringResource

class LoginScreen : Screen {

    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val screenModel = getScreenModel<LoginScreenModel>()
        val state by screenModel.state.collectAsState()
        val snackbarHostState = remember { SnackbarHostState() }
        var passwordVisible by remember { mutableStateOf(false) }

        LaunchedEffect(state.isLoggedIn) {
            if (state.isLoggedIn) {
                navigator.replace(CalendarScreen(initialLocationId = state.initialLocationId))
            }
        }

        LaunchedEffect(state.needsLocationPick) {
            if (state.needsLocationPick) {
                navigator.replace(LocationPickerScreen())
            }
        }

        LaunchedEffect(state.error) {
            state.error?.let { error ->
                snackbarHostState.showSnackbar(error)
                screenModel.clearError()
            }
        }

        Scaffold(
            snackbarHost = { SnackbarHost(snackbarHostState) }
        ) { padding ->
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .padding(padding),
                contentAlignment = Alignment.Center
            ) {
                Column(
                    modifier = Modifier
                        .widthIn(max = Dimens.maxContentWidth)
                        .padding(Dimens.paddingLarge),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Text(
                        text = stringResource(Res.string.app_name),
                        style = MaterialTheme.typography.headlineLarge,
                        color = MaterialTheme.colorScheme.primary,
                        textAlign = TextAlign.Center
                    )

                    Spacer(modifier = Modifier.height(Dimens.spacingSmall))

                    Text(
                        text = stringResource(Res.string.login_subtitle),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        textAlign = TextAlign.Center
                    )

                    Spacer(modifier = Modifier.height(Dimens.spacingXxLarge))

                    ElevatedCard(
                        modifier = Modifier.fillMaxWidth(),
                        elevation = CardDefaults.elevatedCardElevation(defaultElevation = 4.dp)
                    ) {
                        Column(
                            modifier = Modifier.padding(Dimens.paddingLarge),
                            verticalArrangement = Arrangement.spacedBy(Dimens.spacingLarge)
                        ) {
                            OutlinedTextField(
                                value = state.email,
                                onValueChange = screenModel::onEmailChange,
                                label = { Text(stringResource(Res.string.label_email)) },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(
                                    keyboardType = KeyboardType.Email,
                                    imeAction = ImeAction.Next
                                ),
                                enabled = !state.isLoading
                            )

                            OutlinedTextField(
                                value = state.password,
                                onValueChange = screenModel::onPasswordChange,
                                label = { Text(stringResource(Res.string.label_password)) },
                                trailingIcon = {
                                    TextButton(onClick = { passwordVisible = !passwordVisible }) {
                                        Text(if (passwordVisible) stringResource(Res.string.action_hide) else stringResource(Res.string.action_show))
                                    }
                                },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                visualTransformation = if (passwordVisible) VisualTransformation.None
                                                      else PasswordVisualTransformation(),
                                keyboardOptions = KeyboardOptions(
                                    keyboardType = KeyboardType.Password,
                                    imeAction = ImeAction.Done
                                ),
                                keyboardActions = KeyboardActions(
                                    onDone = { screenModel.signIn() }
                                ),
                                enabled = !state.isLoading
                            )

                            TextButton(
                                onClick = screenModel::showForgotPassword,
                                modifier = Modifier.align(Alignment.End)
                            ) {
                                Text(stringResource(Res.string.action_forgot_password))
                            }

                            Button(
                                onClick = screenModel::signIn,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(Dimens.buttonHeightPrimary),
                                enabled = !state.isLoading
                            ) {
                                if (state.isLoading) {
                                    SmallLoadingIndicator()
                                } else {
                                    Text(stringResource(Res.string.action_sign_in))
                                }
                            }
                        }
                    }
                }
            }
        }

        if (state.showForgotPassword) {
            ForgotPasswordDialog(
                email = state.forgotPasswordEmail,
                onEmailChange = screenModel::onForgotPasswordEmailChange,
                onSend = screenModel::sendPasswordReset,
                onDismiss = screenModel::hideForgotPassword,
                isLoading = state.isLoading,
                isSent = state.forgotPasswordSent
            )
        }
    }
}

@Composable
private fun ForgotPasswordDialog(
    email: String,
    onEmailChange: (String) -> Unit,
    onSend: () -> Unit,
    onDismiss: () -> Unit,
    isLoading: Boolean,
    isSent: Boolean
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(if (isSent) stringResource(Res.string.dialog_title_email_sent) else stringResource(Res.string.dialog_title_reset_password))
        },
        text = {
            if (isSent) {
                Text(stringResource(Res.string.msg_reset_email_sent))
            } else {
                Column {
                    Text(stringResource(Res.string.msg_reset_email_prompt))
                    Spacer(modifier = Modifier.height(Dimens.spacingLarge))
                    OutlinedTextField(
                        value = email,
                        onValueChange = onEmailChange,
                        label = { Text(stringResource(Res.string.label_email)) },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Email
                        ),
                        enabled = !isLoading
                    )
                }
            }
        },
        confirmButton = {
            if (isSent) {
                Button(onClick = onDismiss) {
                    Text(stringResource(Res.string.action_ok))
                }
            } else {
                Button(
                    onClick = onSend,
                    enabled = !isLoading
                ) {
                    if (isLoading) {
                        SmallLoadingIndicator()
                    } else {
                        Text(stringResource(Res.string.action_send_reset_link))
                    }
                }
            }
        },
        dismissButton = {
            if (!isSent) {
                TextButton(onClick = onDismiss) {
                    Text(stringResource(Res.string.action_cancel))
                }
            }
        }
    )
}
